<template>
    <AddNoteModal
        :rp-name="rpName"
        :submitting="isCreating"
        :error="error"
        @submit="onSubmit"
        @close="$emit('abort')"
    />
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { ENTITY_DETAILS_TABS } from '@/modules/entity-details/constants/entityDetailsTabs.constants';
    import { useEntityEventsStore } from '@/modules/entity-events/store';
    import AddNoteModal from '@/modules/entity-notes/components/AddNoteModal/AddNoteModal.vue';
    import { useEntityTagsStore } from '@/modules/entity-tags/store';
    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';

    // PRODUCT-26110 QA fixes4: declares every event this procedure component `$emit`s --
    // without a declared `emits`, Vue 3 warns "Component emitted event 'abort'/'finish'
    // but it is neither declared in the emits option nor as an 'onAbort'/'onFinish' prop"
    // every time it fires.
    export default defineComponent({
        name: 'AddNoteProcedure',
        components: { AddNoteModal },
        props: {
            // PRODUCT-26110 QA fixes4: `uuid` is unused; declared purely so Vue 3 doesn't
            // warn "Extraneous non-props attributes (uuid)" (see
            // UploadEntityDocumentProcedure.vue).
            uuid: {
                type: String,
                default: '',
            },
            rpName: {
                type: String as PropType<string>,
                required: true,
            },
            entityId: {
                type: String as PropType<string>,
                required: true,
            },
            entityDisplayName: {
                type: String as PropType<string>,
                default: '',
            },
        },
        emits: [ 'abort', 'finish' ],
        data() {
            return {
                isCreating: false,
                error: '',
            };
        },
        methods: {
            async onSubmit({ text, tags }: { text: string, tags: string[] }): Promise<void> {
                this.isCreating = true;
                this.error = '';
                try {
                    const gatewayClient = (this as any).$gatewayClient;
                    await gatewayClient.tprmUi().createEntityNote(
                        this.rpName, this.entityId, { text, tags },
                    );
                    useSnackbarStore().displaySnackbar({
                        message: `Note added for ${this.entityDisplayName}`,
                        success: true,
                    });
                    if ((this as any).$route?.query?.tab === ENTITY_DETAILS_TABS.EVENTS) {
                        useEntityEventsStore().loadActivityHistory({
                            rpName: this.rpName,
                            entityId: this.entityId,
                            filters: {},
                        });
                        useEntityTagsStore().loadTags({ rpName: this.rpName });
                    }
                    this.$emit('finish', this.entityDisplayName);
                } catch (e) {
                    this.error = 'Something went wrong. Please try again.';
                } finally {
                    this.isCreating = false;
                }
            },
        },
    });
</script>
